import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  Chip,
  Stack,
  Badge,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material"; 
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { useQuery, useMutation } from "react-query";
import client from "../../../../../API";
import ChatScreen from "../../../../../Components/ChatScreen";
import ParticipantsPanel from "../../../../../Components/ChatComponents/chatextra";

export default function CreatorChatDrawer({
  open,
  onClose,
  campaignId,
  creatorId,
  creatorName,
  creatorPfp,
}) {
  const chatRef = useRef(null);

  /* ------------------------------------------------------------------
   * 1. bootstrap / create conversation
   * ----------------------------------------------------------------- */
  const { data: check, refetch } = useQuery(
    ["checkConversation", campaignId, creatorId],
    () =>
      client.conversations.checkCampaign({
        campaign_id: campaignId,
        creator_id: creatorId,
      }),
    { enabled: open }
  );

  const createConversation = useMutation(client.conversations.createCampaign, {
    onSuccess: () => refetch(),
  });

  useEffect(() => {
    if (open && check && !check.conversation_id && !createConversation.isLoading)
      createConversation.mutate({ campaign_id: campaignId, creator_id: creatorId });
  }, [open, check]);

  const conversationId = check?.conversation_id;

  /* ------------------------------------------------------------------
   * 2. pull campaign + creator details (for chips)
   * ----------------------------------------------------------------- */
  const { data: campaignData } = useQuery(
    ["campaignForChat", campaignId],
    () => client.campaigns.details(campaignId),
    { enabled: open }
  );

  const creatorData = useMemo(() => {
    if (!campaignData) return undefined;
    return campaignData.creators.find((c) => c.id === creatorId);
  }, [campaignData, creatorId]);

  const parentVideos = creatorData
    ? creatorData.videos.filter((v) => v.parentId == null)
    : [];
  const crossposts = creatorData
    ? creatorData.videos.filter((v) => v.parentId != null)
    : [];

  /* ------------------------------------------------------------------
   * 3. participants drawer toggle
   * ----------------------------------------------------------------- */
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const toggleParticipants = () => setParticipantsOpen((p) => !p);

  /* ------------------------------------------------------------------
   * 4. badge for ineligible deliverables
   * ----------------------------------------------------------------- */
  const [ineligibleCount, setIneligibleCount] = useState(0);
 const theme = useTheme();
 const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <>
      {/* =============== MAIN CHAT SHEET =============== */}
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { height: "82vh", borderTopLeftRadius: 8, borderTopRightRadius: 8 },
        }}
      >
        {/* -------- header -------- */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderBottom: "1px solid #ddd",
          }}
        >
          <Avatar src={creatorPfp} alt={creatorId} />
          <Typography variant="h6" noWrap flex={1}>
            Chat – @{creatorName}
          </Typography>

          {/* deliverable badge */}
          <IconButton
            color="secondary"
            disabled={ineligibleCount === 0}
            onClick={() => chatRef.current?.scrollToFirstIneligibleVideo?.()}
          >
            <Badge
              badgeContent={ineligibleCount}
              color="error"
              invisible={ineligibleCount === 0}
            >
              <AssignmentTurnedInIcon />
            </Badge>
          </IconButton>

          {/* participants toggle */}
          {conversationId && (
            <IconButton color="primary" onClick={toggleParticipants}>
              <PersonAddIcon />
            </IconButton>
          )}

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* -------- chips row (price + promos) -------- */}
        {creatorData && (
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Chip
              color="primary"
              label={`Offer: ${Number(creatorData.price).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
            />
            {parentVideos.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ ml: 1 }}>
                  Promotions&nbsp;({parentVideos.length}):
                </Typography>
                {parentVideos.map((v) => (
                  <Chip
                    key={v.id}
                    color="primary"
                    label={`${v.promotionPlatform} – ${v.promotionType}`}
                  />
                ))}
              </>
            )}
            {crossposts.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ ml: 1 }}>
                  Crossposts:
                </Typography>
                {crossposts.map((v) => (
                  <Chip
                    key={v.id}
                    color="secondary"
                    label={v.promotionPlatform}
                  />
                ))}
              </>
            )}
          </Box>
        )}

        {/* -------- chat body -------- */}
        {conversationId ? (
          <ChatScreen
            ref={chatRef}
            conversationId={conversationId}
            creatorId={creatorId}
            sx={{ flex: 1, minHeight: 0 }}
            onDeliverablesUpdate={(arr) => setIneligibleCount(arr.length)}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Drawer>

      {/* =============== PARTICIPANTS =============== */}
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={participantsOpen}
        onClose={toggleParticipants}
        PaperProps={{
          sx: isMobile
            ? { height: "85vh", borderTopLeftRadius: 8, borderTopRightRadius: 8 }
            : { width: 360, height: "100%" },   /* full-height on desktop */
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <ParticipantsPanel
            conversationId={conversationId}
            onClose={toggleParticipants}
            sx={{ flex: 1, minHeight: 0 }}  
          />
        </Box>
      </Drawer>
    </>
  );
}
